import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import Api from "../../../shared/api/api";
import useCheckoutResult from "../../../shared/hooks/useCheckoutResult";
import BlogPreview from "../components/blog/BlogPreview";
import BookPreview from "../components/books/BookPreview";
import Testimonials from "../components/testimonials/Testimonials";
import NewsletterSignupForm from "../forms/NewsletterSignupForm";
import logo from "../assets/Logo.png";
import "../styles/HomePage.css";

const BLOG_PREVIEW_LIMIT = 5;

export default function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [featuredBook, setFeaturedBook] = useState(null);
  const [latestBlogs, setLatestBlogs] = useState([]);
  const checkoutStatus = useCheckoutResult(location.search);
  const [contentStatus, setContentStatus] = useState("loading");

  useEffect(() => {
    let isMounted = true;

    async function fetchHomepageContent() {
      setContentStatus("loading");

      const [featuredBookResult, blogsResult] =
        await Promise.allSettled([
          Api.getFeaturedBook(),
          Api.getAllAuthor("blog"),
        ]);

      if (!isMounted) return;

      let requestSucceeded = false;

      if (featuredBookResult.status === "fulfilled") {
        const response = featuredBookResult.value;

        const book =
          response?.featuredBook ||
          response?.book ||
          response?.data ||
          (Array.isArray(response) ? response[0] : null);

        setFeaturedBook(book || null);
        requestSucceeded = true;
      } else {
        console.error(
          "Error loading featured book:",
          featuredBookResult.reason
        );
        setFeaturedBook(null);
      }

      if (blogsResult.status === "fulfilled") {
        const response = blogsResult.value;

        const blogRecords = Array.isArray(response)
          ? response
          : response?.blogs || response?.data || [];

        const sortedBlogs = Array.isArray(blogRecords)
          ? [...blogRecords]
              .sort((firstBlog, secondBlog) => {
                const firstDate = new Date(
                  firstBlog?.date || 0
                ).getTime();

                const secondDate = new Date(
                  secondBlog?.date || 0
                ).getTime();

                return secondDate - firstDate;
              })
              .slice(0, BLOG_PREVIEW_LIMIT)
          : [];

        setLatestBlogs(sortedBlogs);
        requestSucceeded = true;
      } else {
        console.error(
          "Error loading homepage blogs:",
          blogsResult.reason
        );
        setLatestBlogs([]);
      }

      setContentStatus(requestSucceeded ? "success" : "error");
    }

    fetchHomepageContent();

    return () => {
      isMounted = false;
    };
  }, []);

  function handleBlogClick(blog) {
    if (!blog?.title) return;

    navigate(`/blog/${encodeURIComponent(blog.title)}`);
  }

  return (
    <main className="homepage-container">
      {checkoutStatus === "success" && (
        <div
          className="checkoutBanner success"
          role="status"
          aria-live="polite"
        >
          Purchase successful! Thank you for your order.
        </div>
      )}

      {(checkoutStatus === "verifying" || checkoutStatus === "processing") && (
        <div className="checkoutBanner success" role="status" aria-live="polite">
          {checkoutStatus === "verifying"
            ? "Confirming your order..."
            : "Payment received. Your order is still processing; your confirmation email will arrive shortly."}
        </div>
      )}

      {checkoutStatus === "error" && (
        <div className="checkoutBanner cancel" role="alert">
          We could not verify that payment was completed. Your cart has been preserved.
        </div>
      )}

      {checkoutStatus === "cancel" && (
        <div
          className="checkoutBanner cancel"
          role="status"
          aria-live="polite"
        >
          Checkout was canceled.
        </div>
      )}

      <section
        className="hero-section"
        aria-labelledby="homepage-heading"
      >
        <img
          className="hero-logo"
          src={logo}
          alt="Avwakin Publishing"
          decoding="async"
        />

        <h1 id="homepage-heading" className="sr-only">
          Kenneth L. Powell
        </h1>

        <p>Fantasy worlds reborn in dragonfire and betrayal.</p>

        <Link to="/books" className="cta-button">
          Discover My Books
        </Link>
      </section>

      {contentStatus === "loading" && (
        <p className="loadingMsg" role="status">
          Loading the latest books and news...
        </p>
      )}

      {contentStatus === "error" && (
        <p className="warningError" role="alert">
          Some homepage content could not be loaded. Please try again
          later.
        </p>
      )}

      {featuredBook && (
        <>
          <section
            className="featured-book"
            aria-labelledby="featured-book-heading"
          >
            <h2 id="featured-book-heading">Featured Book</h2>

            <BookPreview
              id={featuredBook.id || featuredBook._id}
              title={featuredBook.title || "Featured Book"}
              coverImage={featuredBook.image}
            />
          </section>

          <hr className="section-divider" aria-hidden="true" />
        </>
      )}

      <section
        className="news-section"
        aria-labelledby="latest-news-heading"
      >
        <h2 id="latest-news-heading">Latest News &amp; Lore</h2>

        {contentStatus !== "loading" && latestBlogs.length === 0 ? (
          <p className="homepage-empty-message">
            No recent blog posts are currently available.
          </p>
        ) : (
          <div className="blogGrid">
            {latestBlogs.map((blog) => {
              const blogKey =
                blog?.id ||
                blog?._id ||
                `${blog?.title || "untitled"}-${
                  blog?.date || "undated"
                }`;

              return (
                <BlogPreview
                  key={blogKey}
                  blog={blog}
                  onClick={() => handleBlogClick(blog)}
                />
              );
            })}
          </div>
        )}

        <Link to="/blog" className="view-more">
          View All
        </Link>
      </section>

      <hr className="section-divider" aria-hidden="true" />

      <section
        className="email-signup-section"
        aria-labelledby="newsletter-heading"
      >
        <h2 id="newsletter-heading">Join the Rebellion</h2>

        <p>
          Sign up for my newsletter and receive the full <em>Dawn of Prophecy</em> ebook <strong>ABSOLUTELY FREE</strong>.
        </p>
        <p>I'll also ask you to share an honest review on Goodreads and Amazon after you've read it. Reviews are optional, and your free copy is yours either way.</p>
        <NewsletterSignupForm resetConsentOnSuccess />
      </section>

      <hr className="section-divider" aria-hidden="true" />

      <section
        className="testimonials-section"
        aria-labelledby="testimonials-heading"
      >
        <h2 id="testimonials-heading">
          What Readers Are Saying
        </h2>

        <Testimonials />
      </section>
    </main>
  );
}
